import { call, put, select } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectActiveAddress,
  selectConfig,
  selectCurrentAddressData,
  selectFidelityMaximizerState,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { saveAddress } from '@/containers/SubscriptionFormPageNew/Components/Address/AddressModuleSagas'
import { loadFidelityMaximizerStatus } from '@/containers/SubscriptionFormPageNew/Components/FidelityMaximizer/FidelityMaximizerSagas'
import {
  resetElectricityMeterDirty,
  resetGasMeterDirty,
  updateAddressData,
  updateElectricityMeterAddressData,
  updateGasMeterAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { t } from 'i18next'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import * as Sentry from '@sentry/react'
import { SECOND_ELECTRICITY_METER_KEY } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'

const FIRST_POD_INDEX = 1
const SECOND_POD_INDEX = 2

const createMeterSavePayload = (addressId, meterData, meterType, podIndex = FIRST_POD_INDEX) => {
  const isUpdate = !!meterData.id

  return {
    addressId,
    meterData: {
      meterType,
      podIndex,
      ...(meterType === 'electricity' && {
        electricityMeter: {
          ...(isUpdate && { id: meterData.id }),
          pdlNo: meterData.pdlNo,
          meterSerialNumber: meterData.meterSerialNumber,
          pdlEligibilityData: meterData.pdlEligibilityData,
          oldOccupant: meterData.oldOccupant,
          consentDate: meterData.consentDate,
        },
      }),
      ...(meterType === 'gas' && {
        gasMeter: {
          ...(isUpdate && { id: meterData.id }),
          pceNo: meterData.pceNo,
          meterSerialNumber: meterData.meterSerialNumber,
          pceEligibilityData: meterData.pceEligibilityData,
          isValidPce: meterData.isValidPce,
          consentDate: meterData.consentDate,
        },
      }),
    },
  }
}

function* saveSecondElectricityMeter({ endpoint, addressId, activeAddress }) {
  const addressData = yield select(selectCurrentAddressData)
  const secondMeter = addressData[SECOND_ELECTRICITY_METER_KEY] ?? {}

  if (!secondMeter.pdlNo) {
    return
  }

  const response = yield call(callApi, {
    endpoint,
    method: 'POST',
    payload: createMeterSavePayload(addressId, secondMeter, 'electricity', SECOND_POD_INDEX),
    showErrorSnackbar: true,
  })

  const podId = response?.data?.id

  if (podId) {
    yield put(
      updateElectricityMeterAddressData({
        data: { id: podId },
        id: activeAddress,
        meterKey: SECOND_ELECTRICITY_METER_KEY,
        _skipDirty: true,
      }),
    )
  }
}

export function* saveMeterShared({ enqueue, meterData, addressId, meterType, skipAddressSave = false }) {
  const { saveAddressPodUrl } = yield select(selectExtraRoutesAuthData)
  const activeAddress = yield select(selectActiveAddress)

  if (!saveAddressPodUrl) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('saveAddressPodUrl endpoint not defined in auth data'))
    }
    return
  }

  const updateAction = meterType === 'electricity' ? updateElectricityMeterAddressData : updateGasMeterAddressData

  try {
    yield put(
      updateAction({
        data: { isSaving: true },
        id: activeAddress,
      }),
    )

    let finalAddressId = addressId

    // When the orchestrator (saveClientTab) has already persisted the address,
    // we must NOT call saveAddress again — that would duplicate the address.
    if (!skipAddressSave && (parseInt(activeAddress) <= 0 || activeAddress === 'new')) {
      try {
        yield call(saveAddress)
        const updatedAddressData = yield select(selectCurrentAddressData)
        finalAddressId = updatedAddressData.id

        if (!finalAddressId) {
          throw new Error('Address save failed - no address ID returned')
        }

        if (enqueue) {
          yield call(showSnackbar, enqueue, {
            type: SNACKBAR_TYPES.SUCCESS,
            message: t('subscriptionForm:snackbar.meter_save.address_updated'),
          })
        }
      } catch (addressSaveError) {
        if (import.meta.env.PROD) {
          Sentry.captureException(new Error('Address save failed during meter save process'), {
            extra: { error: addressSaveError },
          })
        }
        if (enqueue) {
          yield call(showSnackbar, enqueue, {
            type: SNACKBAR_TYPES.ERROR,
            message: t('subscriptionForm:snackbar.meter_save.save_error'),
          })
        }
        return
      }
    }

    const apiPayload = createMeterSavePayload(finalAddressId, meterData, meterType)

    const response = yield call(callApi, {
      endpoint: saveAddressPodUrl,
      method: 'POST',
      payload: apiPayload,
      showErrorSnackbar: true,
    })

    if (meterType === 'electricity') {
      yield call(saveSecondElectricityMeter, {
        endpoint: saveAddressPodUrl,
        addressId: finalAddressId,
        activeAddress,
      })
    }

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: t('subscriptionForm:snackbar.meter_save.meter_saved'),
      })
    }

    const hasMeterKey = meterType === 'electricity' ? 'hasElectricityMeter' : 'hasGasMeter'
    // Side-effect tag on the address ─ not a user edit. Skip dirty marking so
    // the global "unsaved changes" warning doesn't re-appear after a save.
    yield put(updateAddressData({ data: { [hasMeterKey]: true }, id: activeAddress, _skipDirty: true }))

    // API success: reset the dirty flag for the saved meter.
    const resetDirtyAction = meterType === 'electricity' ? resetElectricityMeterDirty : resetGasMeterDirty
    yield put(resetDirtyAction({ id: activeAddress }))

    // call(), not put() - must run to completion regardless of the takeLatest watcher
    const { loaded, enabled } = yield select(selectFidelityMaximizerState)
    const { contactId } = yield select(selectConfig)
    if (loaded && enabled && contactId) {
      yield call(loadFidelityMaximizerStatus, { contactId })
    }

    return response
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error(`save${meterType}MeterError`), { extra: { error } })
    }

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: t('subscriptionForm:snackbar.meter_save.save_error'),
      })
    }
  } finally {
    yield put(
      updateAction({
        data: { isSaving: false },
        id: activeAddress,
      }),
    )
  }
}
